/**
 *
 * ActionButtons
 *
 */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _get from 'lodash/get';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
  makeSelectClinicMode,
} from 'containers/Main/selectors';
import PageIntroBlock from 'components/PageIntroBlock';
import useSiteCopySelector from 'components/useSiteCopySelector';
import MixPanel from 'utils/mixpanelService';
import SurveyContext from './SurveyContext';

const useStyles = makeStyles(theme => ({
  container: {
    padding: '20px 40px',
    [theme.breakpoints.down('600')]: {
      padding: 20,
    },
  },
  button: {
    display: 'flex',
    padding: '12px 40px',
    borderRadius: 4,
    justifyContent: 'center',
    fontSize: '0.9375rem',
    fontFamily: 'MadaSemiBold',
    minWidth: 120,
    [theme.breakpoints.down('600')]: {
      width: '100%',
      padding: '12px 20px',
    },
  },
  primaryAction: {
    color: 'white',
    backgroundColor: '#01619B',
    '&:hover': {
      backgroundColor: '#0D73B1',
    },
  },
  secondaryAction: {
    border: `1px solid #01619B`,
    color: '#01619B',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#fff',
    },
  },
  oneOptionWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  twoOptionsWrapper: {
    display: 'flex',
    justifyContent: 'center',
    '& > button': {
      marginLeft: 6,
      marginRight: 6,
    },
    '& > button:first-child': {
      marginLeft: 0,
    },
    '& > button:last-child': {
      marginRight: 0,
    },
  },
  threeOptionsWrapper: {
    display: 'flex',
    '& > button': {
      margin: '0 6px',
      [theme.breakpoints.down('600')]: {
        margin: '6px 0',
      },
    },
    '& > button:first-child': {
      marginLeft: 0,
      [theme.breakpoints.down('600')]: {
        marginTop: 0,
      },
    },
    '& > button:last-child': {
      marginRight: 0,
      [theme.breakpoints.down('600')]: {
        marginBottom: '0',
      },
    },
    [theme.breakpoints.down('600')]: {
      flexDirection: 'column',
    },
  },
}));

const ActionButtons = ({
  currentPage,
  isSignedIn,
  onPreviousClick,
  totalPages,
  slug,
  assessmentName,
  assessmentId,
}) => {
  const { onActionClick } = useContext(SurveyContext);
  const clinicMode = useSelector(makeSelectClinicMode());
  const clientDetails = useSelector(makeSelectClientDetails());
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const [assessmentSiteCopy] = useSiteCopySelector(['assessment']);
  const classes = useStyles();

  const trackClick = action => {
    MixPanel.track(action, {
      slug,
      assessmentName,
      assessmentId,
      page: window.location.pathname,
    });
  };

  if (currentPage === totalPages - 1) {
    if (!allowSignIn || clinicMode) {
      return (
        <Grid container justifyContent="center" className={classes.container}>
          <Grid item xs={12} className={classes.twoOptionsWrapper}>
            <ButtonBase
              className={_classNames(classes.button, classes.secondaryAction)}
              onClick={() => onPreviousClick(currentPage)}
            >
              {_get(assessmentSiteCopy, 'pageCopy.backButton')}
            </ButtonBase>
            <ButtonBase
              className={_classNames(classes.button, classes.primaryAction)}
              onClick={() =>
                onActionClick({
                  type: 'onlyResults',
                  pageNo: currentPage,
                })
              }
            >
              {_get(assessmentSiteCopy, 'pageCopy.completeButton')}
            </ButtonBase>
          </Grid>
        </Grid>
      );
    }

    if (!isSignedIn) {
      return (
        <>
          <PageIntroBlock type="common" page="assessment-save-intro" />
          <Grid container className={classes.container}>
            <Grid item xs={12} className={classes.threeOptionsWrapper}>
              <ButtonBase
                className={_classNames(classes.button, classes.secondaryAction)}
                onClick={() => onPreviousClick(currentPage)}
              >
                {_get(assessmentSiteCopy, 'pageCopy.backButton')}
              </ButtonBase>
              {!_get(
                clientDetails,
                'metadata.hideEmailMyResultsButton',
                false,
              ) ? (
                <ButtonBase
                  className={_classNames(classes.button, classes.primaryAction)}
                  onClick={() => {
                    trackClick('Sign Up and Email Results - Clicked');
                    onActionClick({
                      type: 'complete',
                      pageNo: currentPage,
                      shouldSendEmail: true,
                    });
                  }}
                >
                  {_get(assessmentSiteCopy, 'pageCopy.signUpButton')}
                </ButtonBase>
              ) : (
                <ButtonBase
                  className={_classNames(classes.button, classes.primaryAction)}
                  onClick={() =>
                    onActionClick({
                      type: 'complete',
                      pageNo: currentPage,
                    })
                  }
                >
                  {_get(
                    assessmentSiteCopy,
                    'pageCopy.signUpWithoutEmailButton',
                  )}
                </ButtonBase>
              )}
              <ButtonBase
                className={_classNames(classes.button, classes.secondaryAction)}
                onClick={() => {
                  trackClick('Show Results as Guest - Clicked');
                  onActionClick({ type: 'onlyResults', pageNo: currentPage });
                }}
              >
                {_get(assessmentSiteCopy, 'pageCopy.guestButton')}
              </ButtonBase>
            </Grid>
          </Grid>
        </>
      );
    }

    if (currentPage === 0) {
      return (
        <Grid container justifyContent="center" className={classes.container}>
          <Grid item xs={12} className={classes.oneOptionWrapper}>
            <ButtonBase
              className={_classNames(classes.button, classes.primaryAction)}
              onClick={() =>
                onActionClick({ type: 'complete', pageNo: currentPage })
              }
            >
              {_get(assessmentSiteCopy, 'pageCopy.completeButton')}
            </ButtonBase>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container justifyContent="center" className={classes.container}>
        <Grid item xs={12} className={classes.twoOptionsWrapper}>
          <ButtonBase
            className={_classNames(classes.button, classes.secondaryAction)}
            onClick={() => onPreviousClick(currentPage)}
          >
            {_get(assessmentSiteCopy, 'pageCopy.backButton')}
          </ButtonBase>
          <ButtonBase
            className={_classNames(classes.button, classes.primaryAction)}
            onClick={() =>
              onActionClick({ type: 'complete', pageNo: currentPage })
            }
          >
            {_get(assessmentSiteCopy, 'pageCopy.completeButton')}
          </ButtonBase>
        </Grid>
      </Grid>
    );
  }

  if (currentPage === 0) {
    return (
      <Grid container justifyContent="center" className={classes.container}>
        <Grid item xs={12} className={classes.oneOptionWrapper}>
          <ButtonBase
            className={_classNames(classes.button, classes.primaryAction)}
            onClick={() => onActionClick({ pageNo: currentPage })}
          >
            {_get(assessmentSiteCopy, 'pageCopy.nextButton')}
          </ButtonBase>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container justifyContent="center" className={classes.container}>
      <Grid item xs={12} className={classes.twoOptionsWrapper}>
        <ButtonBase
          className={_classNames(classes.button, classes.secondaryAction)}
          onClick={() => onPreviousClick(currentPage)}
        >
          {_get(assessmentSiteCopy, 'pageCopy.backButton')}
        </ButtonBase>
        <ButtonBase
          className={_classNames(classes.button, classes.primaryAction)}
          onClick={() => onActionClick({ pageNo: currentPage })}
        >
          {_get(assessmentSiteCopy, 'pageCopy.nextButton')}
        </ButtonBase>
      </Grid>
    </Grid>
  );
};

ActionButtons.propTypes = {
  currentPage: PropTypes.number,
  isSignedIn: PropTypes.bool,
  onPreviousClick: PropTypes.func,
  totalPages: PropTypes.number,
};

export default ActionButtons;
